import { Component } from '@angular/core';
import moment from 'moment';
import 'moment-timezone';
import { Event } from 'src/app/core/models/events-response';
import { EventService } from 'src/app/core/services/event.service';
import { MobileService } from 'src/app/core/services/mobile-service';
import { filterArray, Options } from 'src/app/core/models/filter';
import { FilterService } from 'src/app/core/services/filter.service';
import { NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'app-home',
  templateUrl: 'home.page.html',
  styleUrls: ['home.page.scss'],
})
export class HomePage {
  allEvents: Event[];
  displayEvents: Event[];
  displayCount: number;
  highestPrice: number;
  latestEventTime: any;
  selectedSortBy: string = 'startDateTime,asc';
  lastSearchBy: string = '';
  appliedFilters: any = [];
  filter = filterArray;
  navigationSubscription;
  buttonClicked: boolean = false;

  constructor(
    private eventService: EventService,
    private mobileService: MobileService,
    private filterService: FilterService,
    private router: Router
  ) {
    this.getEvents();
    this.navigationSubscription = this.router.events.subscribe((e) => {
      //When navigating back here, reapply filters
      if (e instanceof NavigationEnd && e.url == '/' && this.allEvents) {
        this.appliedFilters = this.filter = this.filterService.getAppliedFilters();
        this.lastSearchBy = this.filterService.getSearchTerm();
        this.filterSelected();
      }
    });
  }

  private getEvents() {
    this.eventService
      .getEvents()
      .then((eventSummaries: Event[]) => {
        //Save latest event start time
        this.latestEventTime = Math.max(
          ...eventSummaries
            .filter((event) => event.startDateTime)
            .map((event) => moment.tz(event.startDateTime, 'Pacific/Auckland').valueOf())
        );
        //Save maximum ticket price
        this.highestPrice = Math.max(...eventSummaries.map((event) => event.highestPrice));

        this.allEvents = this.displayEvents = eventSummaries;

        this.filter = this.filterService.buildDefaultFilters(eventSummaries);
        this.filterSelected();
        
        this.displayCount = 2 * this.mobileService.getDisplayedCardsPerRow();
        if (this.isMobile()) {
          this.displayCount = 6;
        }
      })
      .catch((err) => {});
  }

  //Called when search term is entered
  searchEvents(searchTerm: string) {
    this.lastSearchBy = searchTerm;
    this.filterSelected();
  }

  //Called when filter is applied
  applyFilters(filters) {
    this.appliedFilters = filters;
    this.filterSelected();
  }

  private filterActive(filter: any) {
    return filter?.activeFilter == true || false;
  }

  filterSelected() {
    let eventsToDisplay = [];

    let filterStartDate: any;
    let filterEndDate: any;
    let filterMinPrice: number;
    let filterMaxPrice: number;
    let filterOrganisers: string[] = [];
    let filterOnlineEvents: boolean;
    let filterInPersonEvents: boolean;
    let filterTicketsAvailable: boolean;

    //Extract active filter values
    //Date
    if (this.filterActive(this.appliedFilters[Options.Date])) {
      filterStartDate = this.appliedFilters[Options.Date].options['from'];
      filterEndDate = this.appliedFilters[Options.Date].options['to'];
    }
    if (!filterStartDate) {
      filterStartDate = moment().tz('Pacific/Auckland').valueOf(); //now
    } else {
      filterStartDate = moment.tz(filterStartDate, 'Pacific/Auckland').valueOf();
    }
    if (!filterEndDate) {
      filterEndDate = this.latestEventTime;
    } else {
      filterEndDate = moment.tz(filterEndDate, 'Pacific/Auckland').add(1, 'day').valueOf();
    }

    //Price
    if (this.filterActive(this.appliedFilters[Options.Price])) {
      filterMinPrice = this.appliedFilters[Options.Price].options['from'];
      filterMaxPrice = this.appliedFilters[Options.Price].options['to'];
    }
    if (!filterMinPrice) {
      filterMinPrice = 0;
    }
    if (!filterMaxPrice) {
      filterMaxPrice = this.highestPrice;
    }

    //Organisers
    if (this.filterActive(this.appliedFilters[Options.Organisers])) {
      filterOrganisers = this.appliedFilters[Options.Organisers].options
        .filter((option) => option.checked == true)
        .map((option) => option.desc);
    }

    //Location
    if (this.filterActive(this.appliedFilters[Options.Location])) {
      this.appliedFilters[Options.Location].options.forEach((option) => {
        switch (option.code) {
          case 'online':
            filterOnlineEvents = option.checked == true;
            break;
          case 'inPerson':
            filterInPersonEvents = option.checked == true;
            break;
        }
      });
    }
    if (!filterOnlineEvents) {
      filterOnlineEvents = false;
    }
    if (!filterInPersonEvents) {
      filterInPersonEvents = false;
    }

    //Availability
    if (this.filterActive(this.appliedFilters[Options.Availability])) {
      this.appliedFilters[Options.Availability].options.forEach((option) => {
        switch (option.code) {
          case 'available':
            filterTicketsAvailable = option.checked == true;
            break;
        }
      });
    }
    if (!filterTicketsAvailable) {
      filterTicketsAvailable = false;
    }

    // Reset events to include all original events
    // this.displayEvents = this.allEvents;

    eventsToDisplay = this.allEvents.filter((event) => {
      //--Date filter--
      let eventDate = moment.tz(event.startDateTime, 'Pacific/Auckland').valueOf();
      let eventInSelectedDateRange =
        (filterStartDate <= eventDate && eventDate <= filterEndDate) ||
        (!this.filterActive(this.appliedFilters[Options.Date]) && isNaN(eventDate));

      //--Price filter--
      let eventInSelectedPriceRange = event.lowestPrice <= filterMaxPrice && event.highestPrice >= filterMinPrice;

      //--Organiser filter--
      let eventInSelectedOrganisers =
        filterOrganisers.length == 0 || (filterOrganisers.length > 0 && filterOrganisers.includes(event.organisationName));

      //--Location filter--
      //Show Online & In-person events if both options are the same
      let allLocations = filterOnlineEvents == filterInPersonEvents;
      let eventOnline = event.location && event.location.name === 'Online';
      let eventInPerson = event.location && event.location.name !== 'Online';
      let eventInSelectedLocation = allLocations || (filterOnlineEvents && eventOnline) || (filterInPersonEvents && eventInPerson);

      //--Ticket Availability filter--
      let eventHasSelectedAvailability = !filterTicketsAvailable || (filterTicketsAvailable && !event.isSoldOut);

      return (
        eventInSelectedDateRange &&
        eventInSelectedPriceRange &&
        eventInSelectedOrganisers &&
        eventInSelectedLocation &&
        eventHasSelectedAvailability
      );
    });

    // Need to reapply search term as all events have been filtered
    this.searchThroughEvents(eventsToDisplay);
  }

  private searchThroughEvents(eventsToSearch: Event[]) {
    let searchTerm = this.lastSearchBy.toLocaleLowerCase();

    // Assign filtered events for display in frontend
    this.displayEvents = eventsToSearch.filter((e) => {
      e = new Event(e); //Create actual instance of Event
      let eventTime = e.getEventStartTimeDisplay();
      let eventLocation = e.getLocationDisplay();
      let price = e.getPriceDisplay();

      return (
        e.name.toLocaleLowerCase().includes(searchTerm) ||
        e.summary.toLocaleLowerCase().includes(searchTerm) ||
        eventTime.toLocaleLowerCase().includes(searchTerm) ||
        eventLocation.toLocaleLowerCase().includes(searchTerm) ||
        price.toLocaleLowerCase().includes(searchTerm)
      );
    });

    //Finally sort events that have now been filtered & searched
    this.sortEvents();
  }

  sortEvents() {
    var params = this.selectedSortBy.split(',');
    var sortField = params[0];
    var asc = params[1] === 'asc';

    var sortByFieldCompare;
    switch (sortField) {
      case 'name':
        this.displayEvents.sort((a: Event, b: Event) => {
          if (asc) {
            sortByFieldCompare = this.compareStrings(a.name, b.name);
          } else {
            sortByFieldCompare = this.compareStrings(b.name, a.name);
          }
          //If names are the same, then sort by dateTime, then by price
          return (
            sortByFieldCompare ||
            this.compareStrings(a.startDateTime, b.startDateTime) ||
            a.lowestPrice - b.lowestPrice ||
            a.highestPrice - b.highestPrice
          );
        });
        break;
      case 'lowestPrice':
        this.displayEvents.sort((a: Event, b: Event) => {
          if (asc) {
            sortByFieldCompare = a.lowestPrice - b.lowestPrice || a.highestPrice - b.highestPrice;
          } else {
            sortByFieldCompare = b.lowestPrice - a.lowestPrice || b.highestPrice - a.highestPrice;
          }
          //If prices are the same, then sort by name, then by dateTime
          return sortByFieldCompare || this.compareStrings(a.name, b.name) || this.compareStrings(a.startDateTime, b.startDateTime);
        });
        break;
      case 'startDateTime':
        this.displayEvents.sort((a: Event, b: Event) => {
          if (asc) {
            sortByFieldCompare = this.compareStrings(a.startDateTime, b.startDateTime);
          } else {
            sortByFieldCompare = this.compareStrings(b.startDateTime, a.startDateTime);
          }
          //If dateTimes are the same, then sort by name, then by price
          return (
            sortByFieldCompare || this.compareStrings(a.name, b.name) || a.lowestPrice - b.lowestPrice || a.highestPrice - b.highestPrice
          );
        });
        break;
    }
  }

  private compareStrings(str1: String, str2: string): number {
    if (str1 == null && str2 == null) return 0;
    if (str1 == null) return 1;
    if (str2 == null) return -1;
    return str1.localeCompare(str2);
  }

  isMobile(): boolean {
    return this.mobileService.isMobile();
  }

  showMoreEvents() {
    if (this.isMobile()) {
      this.displayCount = this.allEvents.length;
    } else {
      const cardsPerRow = this.mobileService.getDisplayedCardsPerRow();
      this.displayCount += 2 * cardsPerRow;

      //Bump up to fill row in case screen size changed
      this.displayCount = Math.ceil(this.displayCount / cardsPerRow) * cardsPerRow;
    }
  }

  openBreadcrumb() {
    this.buttonClicked = true;
  }

  closeBreadcrumb() {
    this.buttonClicked = false;
  }
}
